import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import DateDisplay from "design-system/molecules/DateDisplay"
import { FlagsBadge } from "design-system/molecules/FlagsBadge"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { ConversationFlagKeyBadge } from "modules/conversations/components/ChatHistory/ConversationFlagKeyBadge"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeFlagsConversationsResponseDataSchema } from "schema"
import { z } from "zod"

type Row = z.infer<typeof BackofficeFlagsConversationsResponseDataSchema>[number]
const columnHelper = createColumnHelper<Features, Row>()

type Props = {
  data: z.infer<typeof BackofficeFlagsConversationsResponseDataSchema> | undefined
  isPending: boolean
  onRowClick: (row: Row) => void
}

export function BackofficeFlagsConversationsTable({ data, isPending, onRowClick }: Props) {
  const { t } = useTranslation()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("lastFlaggedAt", {
          header: t("backoffice.flags.table.lastFlag"),
          cell: ({ getValue }) => <DateDisplay date={getValue()} />,
        }),
        columnHelper.display({
          id: "company",
          header: t("backoffice.flags.table.company"),
          cell: ({ row }) => {
            const { name, logo } = row.original.company
            return (
              <div className="flex items-center gap-2">
                {logo ? <img src={logo} style={{ height: 30 }} /> : null}
                <span>{name ?? "—"}</span>
              </div>
            )
          },
        }),
        columnHelper.display({
          id: "campaign",
          header: t("backoffice.flags.table.campaignName"),
          cell: ({ row }) => row.original.campaign?.name ?? "—",
        }),
        columnHelper.accessor("channel", {
          header: t("backoffice.flags.table.channel"),
          cell: ({ getValue }) => t(`campaigns.channel.${getValue().toLowerCase()}.label`),
        }),
        columnHelper.accessor("assistantMessages", {
          header: t("backoffice.flags.table.assistantMessages"),
          meta: { align: "center" as const },
        }),
        columnHelper.display({
          id: "flags",
          header: t("backoffice.flags.table.flags"),
          cell: ({ row }) => {
            const { issueCount, frictionCount } = row.original
            if (issueCount <= 0 && frictionCount <= 0) return null
            return <FlagsBadge issuesCount={issueCount} frictionsCount={frictionCount} />
          },
          meta: { align: "center" as const },
        }),
        columnHelper.accessor("keys", {
          header: t("backoffice.flags.table.keys"),
          cell: ({ getValue }) => {
            const keys = getValue()

            return (
              <div className="flex flex-wrap items-center gap-1">
                {keys.slice(0, 5).map((entry) => (
                  <ConversationFlagKeyBadge
                    key={`${entry.kind}:${entry.key}`}
                    flagKey={entry.key}
                    kind={entry.kind}
                    count={entry.count}
                  />
                ))}
                {keys.length > 5 && <span className="text-muted-foreground">{"…"}</span>}
              </div>
            )
          },
          meta: { colSpan: 2 },
        }),
      ]),
    [t],
  )

  const table = useTable({ features, data: data ?? [], columns })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }
    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.flags.table.empty")}
        />
      )
    }
    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.id} onClick={() => onRowClick(row.original)} className="cursor-pointer">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
